// overview_manager.jsx
// QR Create top-line dashboard: how many people signed up, what they made, how
// much it gets scanned, what allowance is outstanding and how many accounts are
// actually paying.
//
// Dataviz: the headline numbers are stat tiles (a single value is never a
// one-bar chart). Scans and signups are two SEPARATE single-series trends rather
// than one dual-axis chart — they are different units and putting them on shared
// axes would invent a correlation. Credit buckets are meters against their own
// totals, in the same single hue as every other magnitude mark.

import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Users, UserPlus, QrCode, ScanLine, Sparkles, Coins, RefreshCw, TrendingUp, Package,
} from 'lucide-react';
import { useQRCreate } from '../../authentication_components/qrCreateContext';
import {
  Section, StatTile, TrendBars, Meter, Loading, EmptyState, Button, inputClass,
  fmtInt, fmtMoney, fmtDateTime,
} from './qrcreate_ui';

const WINDOWS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 365, label: 'Last 12 months' },
];

export default function OverviewManager() {
  const { fetchOverview, fetchCreditPacks } = useQRCreate();

  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState(null);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (windowDays, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await fetchOverview({ days: windowDays });
      setOverview(data || null);
    } catch (err) {
      toast.error(err.message || 'Failed to load the QR Create overview');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [fetchOverview]);

  useEffect(() => { load(days); }, [days, load]);

  // The catalog rarely changes and is not tied to the window, so it loads once.
  useEffect(() => {
    fetchCreditPacks()
      .then((data) => setPacks(data?.packs || []))
      .catch(() => { /* the section simply stays empty */ });
  }, [fetchCreditPacks]);

  const refresh = () => {
    setRefreshing(true);
    load(days, { silent: true });
  };

  const windowLabel = WINDOWS.find((w) => w.value === days)?.label.toLowerCase() || `last ${days} days`;

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-3 py-6 sm:px-4 lg:px-8">
      {/* Header + window picker */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-800">QR Create overview</h2>
          <p className="text-sm text-slate-500">
            Registered accounts, generated codes, scan activity and outstanding credits.
            {overview?.generated_at && <span className="text-slate-400"> · updated {fmtDateTime(overview.generated_at)}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className={`${inputClass} w-44`}>
            {WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
          <Button variant="secondary" onClick={refresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Accounts */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Registered users" value={overview?.total_users} icon={Users} loading={loading} />
        <StatTile label="New signups" value={overview?.new_users} hint={windowLabel} icon={UserPlus} loading={loading} />
        <StatTile label="Signed in" value={overview?.active_users} hint={windowLabel} icon={TrendingUp} loading={loading} />
        <StatTile label="Pro subscribers" value={overview?.pro_subscribers} hint="currently entitled" icon={Sparkles} loading={loading} accent />
      </div>

      {/* Codes + scans */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="QR codes" value={overview?.total_codes} icon={QrCode} loading={loading} />
        <StatTile label="Customized codes" value={overview?.customized_codes} hint="credit-funded" icon={Sparkles} loading={loading} />
        <StatTile label="Codes created" value={overview?.new_codes} hint={windowLabel} icon={QrCode} loading={loading} />
        <StatTile label="Scans" value={overview?.window_scans} hint={`${windowLabel} · ${fmtInt(overview?.total_scans)} all time`} icon={ScanLine} loading={loading} accent />
      </div>

      {/* Two separate single-series trends — never one dual-axis chart. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="Scans over time" subtitle={windowLabel} icon={ScanLine}>
          {loading ? <Loading label="Loading scans…" /> : <TrendBars points={overview?.scan_timeline} label="scans" />}
        </Section>

        <Section title="Signups over time" subtitle={windowLabel} icon={UserPlus}>
          {loading ? <Loading label="Loading signups…" /> : <TrendBars points={overview?.signup_timeline} label="signups" emptyLabel="No signups in this window" />}
        </Section>
      </div>

      {/* Credit economy */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="Credit economy" subtitle="Across every account" icon={Coins}>
          {loading ? (
            <Loading label="Loading credits…" />
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Outstanding</p>
                  <p className="text-xl font-extrabold tabular-nums text-slate-800">{fmtInt(overview?.credits_outstanding)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Spent</p>
                  <p className="text-xl font-extrabold tabular-nums text-slate-800">{fmtInt(overview?.credits_spent)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Free quota left</p>
                  <p className="text-xl font-extrabold tabular-nums text-slate-800">{fmtInt(overview?.basic_remaining)}</p>
                </div>
              </div>

              <Meter
                label="Credits spent of all credits ever granted"
                value={overview?.credits_spent || 0}
                total={(overview?.credits_spent || 0) + (overview?.credits_outstanding || 0)}
                hint="Credits buy customized codes; the free plain-code quota is tracked separately and never borrows from them."
              />

              <Meter
                label="Accounts on Pro"
                value={overview?.pro_subscribers || 0}
                total={overview?.total_users || 0}
                hint="Counted by live entitlement, so an expired Pro row is not included."
              />
            </div>
          )}
        </Section>

        <Section title="Credit packs" subtitle="What the app currently sells" icon={Package}>
          {packs.length === 0 ? (
            <EmptyState icon={Package} title="No credit packs configured" hint="Packs live in qr_create_credit_packs." />
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5">Pack</th>
                    <th className="px-4 py-2.5 text-right">Credits</th>
                    <th className="px-4 py-2.5 text-right">Free codes</th>
                    <th className="px-4 py-2.5 text-right">Price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {packs.map((p) => (
                    <tr key={p.pack_id} className={p.is_active ? '' : 'opacity-50'}>
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-slate-700">{p.display_name}</div>
                        <div className="text-xs text-slate-400">{p.pack_id}{!p.is_active && ' · retired'}</div>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{fmtInt(p.credits)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{fmtInt(p.basic_quota)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-slate-700">{fmtMoney(p.price_cents, p.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
